import {
  Product,
  ProductPrice,
  ProductSlug,
  ProductType,
  ShippingInfo,
  SkuOption,
} from "@/api/product-api";
import { CurrencyIsoCode } from "@/enums";
import { EUROZONE_COUNTRY_CODES } from "@/utils/CountryUtils";
import { GameResell } from "@/models/GameResell";
import { ProductInfo } from "@/services/cart-service/types";
import { CartUtil } from "@/utils/CartUtil";
import { format } from "date-fns";
import { GameUtil } from "@/utils/GameUtil";
import { isBundleOnlyGameResell } from "@/libs/games/bundle-only-game-key";
import { isSetVariantSlug, setBaseOf } from "@/libs/product/set-variant";
import { sizeLineOf, sizeLineRepresentative } from "@/libs/product/size-line";
const BASE_URL = "https://cdn.bhaptics.com/images/products";

const arizonaOnyx0 = `${BASE_URL}/bundle/arizona-series-bundle-onyx-thumbnail@1x.png`;
const arizonaAsh0 = `${BASE_URL}/bundle/arizona-series-bundle-ash-thumbnail@1x.png`;
const airOnyx0 = `${BASE_URL}/air-01.png`;
const airOnyx1 = `${BASE_URL}/air-03.png`;
const airOnyx2 = `${BASE_URL}/air-05.png`;
const airOnyx3 = `${BASE_URL}/air-06.png`;
const airAsh0 = `${BASE_URL}/air-02.png`;
const airAsh1 = `${BASE_URL}/air-04.png`;
const airAsh2 = `${BASE_URL}/air-05.png`;
const airAsh3 = `${BASE_URL}/air-07.png`;
const airCamo0 = `${BASE_URL}/air-camo-01.png`;
const airCamo1 = `${BASE_URL}/air-camo-02.png`;
const airCamo2 = `${BASE_URL}/air-camo-03.png`;
const airCamo3 = `${BASE_URL}/air-camo-04.png`;
const airCamo4 = `${BASE_URL}/air-camo-05.png`;
const starterOnyx0 = `${BASE_URL}/starter-bundle-onyx.png`;
const starterAsh0 = `${BASE_URL}/starter-bundle-ash.png`;
const sleeve0 = `${BASE_URL}/sleeves-0.png`;
const sleeve1 = `${BASE_URL}/sleeves-2.png`;
const gloveCompare = `${BASE_URL}/tactglove-dk2/glove-compare.png`;
const gloveCharcoal0 = `${BASE_URL}/tactglove-dk2/glove-charcoal-0.png`;
const gloveCharcoal1 = `${BASE_URL}/tactglove-dk2/glove-charcoal-1.png`;
const gloveCharcoal2 = `${BASE_URL}/tactglove-dk2/glove-charcoal-2.png`;
const gloveBrown0 = `${BASE_URL}/tactglove-dk2/glove-brown-0.png`;
const gloveBrown1 = `${BASE_URL}/tactglove-dk2/glove-brown-1.png`;
const gloveBrown2 = `${BASE_URL}/tactglove-dk2/glove-brown-2.png`;

const ONYX_SET = [airOnyx1, airOnyx2, airOnyx3];
const ASH_SET = [airAsh1, airAsh2, airAsh3];
const CAMO_SET = [airCamo1, airCamo2, airCamo3, airCamo4];
const SLEEVE_SET = [sleeve0, sleeve1];
const CHARCOAL_GLOVE_SET = [
  gloveCharcoal0,
  gloveCharcoal1,
  gloveCharcoal2,
  gloveCompare,
];
const BROWN_GLOVE_SET = [gloveBrown0, gloveBrown1, gloveBrown2, gloveCompare];

const OVERRIDE_IMAGES: Partial<
  Record<ProductSlug, Partial<Record<string, string[]>>>
> = {
  [ProductSlug.STARTER_BUNDLE]: {
    onyx: [starterOnyx0, airOnyx0, ...ONYX_SET, ...SLEEVE_SET],
    ash: [starterAsh0, airAsh0, ...ASH_SET, ...SLEEVE_SET],
  },
  [ProductSlug.ARIZONA_BUNDLE]: {
    onyx: [arizonaOnyx0, airOnyx0, ...ONYX_SET],
    ash: [arizonaAsh0, airAsh0, ...ASH_SET],
  },
  [ProductSlug.TACTSUIT_AIR]: {
    onyx: [airOnyx0, ...ONYX_SET],
    ash: [airAsh0, ...ASH_SET],
    camo: [airCamo0, ...CAMO_SET],
  },
  [ProductSlug.TACTGLOVE_DK2]: {
    charcoal: CHARCOAL_GLOVE_SET,
    brown: BROWN_GLOVE_SET,
  },
  [ProductSlug.TACTGLOVE_DK2_BUNDLE]: {
    charcoal: CHARCOAL_GLOVE_SET,
    brown: BROWN_GLOVE_SET,
  },
};

const MADE_FOR_META_DEVICES = [
  ProductSlug.TACTSUIT_PRO,
  ProductSlug.TACTSUIT_AIR,
  // ProductSlug.TACTSUIT_AIR_CAMO,
];

const getDeviceBodyPart = (
  slug: string,
): "Torso" | "Arms" | "Hands" | "Feet" | "Head" | "Etc" => {
  switch (slug) {
    case ProductSlug.TACTSUIT_PRO:
    case ProductSlug.TACTSUIT_AIR_CAMO:
    case ProductSlug.TACTSUIT_AIR:
    case ProductSlug.STARTER_BUNDLE:
    case ProductSlug.FPS_BUNDLE:
    case ProductSlug.FPS_BUNDLE_TACTVISOR:
      return "Torso";
    case ProductSlug.TACTSLEEVE:
      return "Arms";
    case ProductSlug.TACTGLOVE_DK2:
    case ProductSlug.TACTGLOVE_DK2_BUNDLE:
    case ProductSlug.TACTGLOVE_DK3:
    case ProductSlug.TACTGLOVE_DK3_BUNDLE:
      return "Hands";
    case ProductSlug.TACTOSY_FOR_FEET:
      return "Feet";
    case ProductSlug.TACTVISOR:
      return "Head";
    default:
      return "Etc";
  }
};

const TACTSUIT_SLUGS: string[] = [
  ProductSlug.TACTSUIT_PRO,
  ProductSlug.TACTSUIT_AIR,
  ProductSlug.TACTSUIT_AIR_ASH,
  ProductSlug.TACTSUIT_AIR_ONYX,
  ProductSlug.TACTSUIT_AIR_CAMO,
];

const GLOVE_SLUGS: string[] = [
  ProductSlug.TACTGLOVE_DK2,
  ProductSlug.TACTGLOVE_DK3,
];

const getAccessoryCategory = (
  product: Product,
): "TactSuit" | "Glove" | "Etc" => {
  if (product.accessoryFor.some((slug) => TACTSUIT_SLUGS.includes(slug)))
    return "TactSuit";
  if (product.accessoryFor.some((slug) => GLOVE_SLUGS.includes(slug)))
    return "Glove";
  return "Etc";
};

const BUNDLE_CATEGORIES = {
  [ProductSlug.TACTSUIT_PRO]: "TactSuit Pro Set",
  [ProductSlug.TACTSUIT_AIR]: "TactSuit Air Set",
  [ProductSlug.TACTSUIT_AIR_ASH]: "TactSuit Air Set",
  [ProductSlug.TACTSUIT_AIR_ONYX]: "TactSuit Air Set",
  [ProductSlug.TACTSUIT_AIR_CAMO]: "TactSuit Air Set",
};

// =============================================
// SKU 기준 핵심 함수들
// =============================================
const getSkuOption = (product: Product, sku: string): SkuOption | undefined => {
  return product.skuOptions.find((option) => option.sku === sku);
};

const getStockBySku = (product: Product, sku: string): number => {
  return getSkuOption(product, sku)?.stock ?? 0;
};

const getShippingInfoBySku = (
  product: Product,
  sku: string,
): ShippingInfo | null => {
  return getSkuOption(product, sku)?.shippingInfo ?? null;
};

// 제품 전체 sold out 체크
const isProductSoldOut = (product?: Product): boolean => {
  if (!product) return false;
  return product.skuOptions.every((option) => option.stock <= 0);
};

const BODYPART_PROIRITY: Record<string, number> = {
  Torso: 1,
  Arms: 2,
  Head: 3,
  Hands: 4,
  Feet: 5,
  Etc: 6,
};

const PRODUCT_PRIORITY: Record<string, number> = {
  [ProductSlug.TACTSUIT_PRO]: 1,
  [ProductSlug.TACTSUIT_AIR]: 2,
  [ProductSlug.TACTSUIT_AIR_CAMO]: 3,
  [ProductSlug.TACTGLOVE_DK3]: 4,
};

// DK3 출시로 DK2 는 GNB / 카테고리 목록에서 노출 제외. (단품/번들 모두)
// admin unpublish 전까지 코드 차원에서 명시 hide.
const HIDDEN_DEVICE_SLUGS: string[] = [
  ProductSlug.TACTGLOVE_DK2,
  ProductSlug.TACTGLOVE_DK2_BUNDLE,
];

const categorizeDevices = (products: Product[]) => {
  const devices = products
    .filter(
      (p) =>
        p.productType === ProductType.DEVICE &&
        !HIDDEN_DEVICE_SLUGS.includes(p.slug),
    )
    .sort((a, b) => {
      return (
        (PRODUCT_PRIORITY[a.slug] ?? 99) - (PRODUCT_PRIORITY[b.slug] ?? 99)
      );
    });
  const categoryMap = new Map<string, Product[]>();

  devices.forEach((product) => {
    const bodyPart = getDeviceBodyPart(product.slug);
    const existing = categoryMap.get(bodyPart);
    if (existing) {
      existing.push(product);
    } else {
      categoryMap.set(bodyPart, [product]);
    }
  });

  return Array.from(categoryMap.entries())
    .map(([key, value]) => ({
      products: value,
      category: key,
    }))
    .sort((a, b) => {
      return BODYPART_PROIRITY[a.category] - BODYPART_PROIRITY[b.category];
    });
};

// 기존 형식을 지원합니다.
// overrideImages 가 있다면 해당 이미지를 우선으로 반환합니다.
const getProductImages = ({
  product,
  sku,
  options = [],
}: {
  product: Product;
  sku?: string;
  options?: { type: string; value: string }[];
}) => {
  const skuOption = product.skuOptions.find((o) => o.sku === sku);

  const overrideImages = getOverrideImages({ slug: product.slug, options });
  if (overrideImages) return overrideImages;

  const skuProductImages = skuOption?.productImages ?? [];
  if (skuProductImages.length > 0) return skuProductImages;

  const skuThumbnail = skuOption?.thumbnail;
  if (skuThumbnail) return [skuThumbnail];

  const productImages = product.productImages ?? [];
  if (productImages.length > 0) return productImages;

  return [product.thumbnail];
};

const getOverrideImages = ({
  slug,
  options,
}: {
  slug: ProductSlug;
  options: { type: string; value: string }[];
}): string[] | undefined => {
  const colorKey = options
    .find((o) => o.type === "color")
    ?.value?.toLowerCase();
  if (!colorKey) {
    return undefined;
  }
  return OVERRIDE_IMAGES[slug]?.[colorKey];
};

const categorizeTactSuitAccessories = (
  products: Product[],
  options?: AccessoryListingOptions,
): Record<"all" | "pro" | "air" | "glove" | "allWithoutGlove", Product[]> => {
  const accessories = getAccessories(products, options);

  const isForGlove = (p: Product) =>
    p.accessoryFor.includes(ProductSlug.TACTGLOVE_DK2) ||
    p.accessoryFor.includes(ProductSlug.TACTGLOVE_DK3);

  return {
    all: accessories,
    pro: accessories.filter((p) =>
      p.accessoryFor.includes(ProductSlug.TACTSUIT_PRO),
    ),
    air: accessories.filter((p) =>
      p.accessoryFor.includes(ProductSlug.TACTSUIT_AIR),
    ),
    glove: accessories.filter(isForGlove),
    allWithoutGlove: accessories.filter((p) => !isForGlove(p)),
  };
};

const categorizeBundles = (bundles: Product[]) => {
  const categoryMap = new Map<string, Product[]>();

  bundles.forEach((product) => {
    const firstMatchingSku = product.skuOptions
      .flatMap((option) => option.components)
      .find((sku) => BUNDLE_CATEGORIES[sku as ProductSlug]);

    if (!firstMatchingSku) return;

    const category = BUNDLE_CATEGORIES[firstMatchingSku as ProductSlug];
    const existing = categoryMap.get(category);

    if (existing) {
      existing.push(product);
    } else {
      categoryMap.set(category, [product]);
    }
  });

  return Array.from(categoryMap.entries()).map(([key, value]) => ({
    products: value,
    category: key,
  }));
};
const categorizeAccessories = (
  products: Product[],
  options?: AccessoryListingOptions,
) => {
  const accessories = getAccessories(products, options);
  const categoryMap = new Map<string, Product[]>();

  accessories.forEach((product) => {
    const category = getAccessoryCategory(product);
    const existing = categoryMap.get(category);
    if (existing) {
      existing.push(product);
    } else {
      categoryMap.set(category, [product]);
    }
  });

  return Array.from(categoryMap.entries()).map(([key, value]) => ({
    products: value,
    category: key,
  }));
};

const getProductGameResellIds = (product?: Product | null): string[] => {
  if (!product) return [];
  const skuOptions = product.skuOptions;
  if (!skuOptions || skuOptions.length === 0) {
    return [];
  }
  const ids = skuOptions.flatMap(
    (option) => option.gameResells?.map((gr) => gr.id) ?? [],
  );
  return Array.from(new Set(ids));
};

/**
 * mgmt 는 **재고가 없어 출고일이 없는 SKU** 에 `estimatedShippingDate: "9999-12-31"` 을 표지로 보낸다
 * (prod 2026-10-01: 9999 날짜 SKU 5개 = 재고 0 SKU 5개, 1:1). 날짜가 아니다 — 그대로 그리면 PDP 에
 * 「Est. Ship Date: December 31, 9999」가 뜨고, 「가장 늦은 출고일」을 고르는 집계(장바구니·draft)를 덮는다.
 */
const SHIPPING_DATE_SENTINEL_YEAR = 9999;

/**
 * SKU 의 출고 예정일 — 표지(9999년 이후)와 빈 값은 `undefined`. ★출고일을 읽는 곳은 전부 이걸 쓴다
 * (PDP `EstShippingDate` · 장바구니 행 · 장바구니/draft 집계 3곳).
 */
const getSkuShippingDate = (
  skuOption?: { shippingInfo?: { estimatedShippingDate?: string } } | null,
): string | undefined => {
  const value = skuOption?.shippingInfo?.estimatedShippingDate;
  if (!value) return undefined;
  // 앞자리 숫자 전부가 연도다 — `slice(0, 4)` 는 5자리 연도를 1000 으로 읽는다.
  const year = Number(/^(\d+)/.exec(String(value))?.[1]);
  return Number.isFinite(year) && year >= SHIPPING_DATE_SENTINEL_YEAR
    ? undefined
    : value;
};

const getEstimatedShippingTime = (items: ProductInfo[]): string => {
  const baseDate = format(new Date(), "yyyy-MM-dd");
  if (!items || items.length === 0) {
    return baseDate;
  }

  const shippingDates = items
    .map((item) => {
      const skuOption = item.skuOptions.find((opt) => opt.sku === item.sku);
      return getSkuShippingDate(skuOption);
    })
    .filter((date): date is string => !!date);

  if (shippingDates.length === 0) {
    return baseDate;
  }

  return shippingDates.reduce((latest, current) =>
    current > latest ? current : latest,
  );
};

/**
 * 기존 레퍼러의 경우 고객이 지불하는가격(원가 - 할인금액)과 레퍼러 적용된 가격(원가(1-레퍼러할인)중에서 더 작은 가격을 받는 방식이었음.
 * 25년 11월 24일 기준으로.
 * 레퍼러 할인이 있는경우 고객이 지불하는 가격(원가 - 할인금액) * (1-레퍼러할인) 가격을 받는 방식으로 변경되었습니다.
 */
const getPrice = ({
  currency,
  prices,
  furtherDiscountPercentile,
}: {
  currency: CurrencyIsoCode;
  prices: ProductPrice[];
  furtherDiscountPercentile?: number; // (정가 - 할인) * furtherDiscountPercentile = 실제 할인 금액
}): ProductPrice | null => {
  const price = prices.find((price) => price.currency === currency);

  // ★정가(`originalAmount`)가 0 이면 **미등록**이다 — mgmt 는 미등록 통화·tier 에도 행을 빼지 않고
  // `originalAmount: 0` 을 채워 보낸다. 그대로 쓰면 ₩0·¥0·$0 이 노출·장바구니·주문까지 통과한다.
  // KR·JP·비-US USD(기준가 `prices[]`)가 이 한 곳으로 막힌다(EU/GB/US 는 위 tier 분기에서도 막는다).
  // ⚠️ **실판매가 0(= 100% 할인 무료 라인)은 통과**시킨다 — 정가가 있는 의도된 무료 항목이고, 네이버페이
  // 헬퍼도 같은 이유로 허용한다(`naverpay-base-price.ts`). 막는 건 「정가 없음」 하나다.
  if (!price || !(price.originalAmount > 0)) {
    return null;
  }

  const displayAmount = price.displayAmount ?? price.originalAmount;
  const discountedOriginalPrice = price.originalAmount - price.discountAmount;
  const furtherDiscountAmount = furtherDiscountPercentile
    ? Math.round(discountedOriginalPrice * furtherDiscountPercentile)
    : 0;

  return {
    ...price,
    displayAmount,
    discountAmount: price.discountAmount + furtherDiscountAmount,
  };
};

// 지역 전용 가격은 `prices[]` 가 아니라 **별도 필드**로 관리 — US=`usPrice`, EU(유로존)=`euPrice`,
// GB=`gbPrice`. currency 만으로 분기 못 한다(CA/AU 등도 USD currency)라서 countryCode 로 고른다.
//
// ⚠️ 여기서 고른 가격의 통화는 `CountryUtils.convertCountryCodeToCurrency` 가 고른 통화와
// **반드시 일치**해야 한다 — `getPrice` 가 `prices.find(p => p.currency === currency)` 정확 매칭이라
// 어긋나면 `null`(표시 사라짐)이 되고, Fixed 로 선언된 채 USD 값이 나가면 과다청구(#4975)가 난다.
// 국가 집합의 단일 출처는 `EUROZONE_COUNTRY_CODES`. docs/pricing-tiers.md
const getCountryAwarePrices = ({
  product,
  countryCode,
  currency,
}: {
  product:
    Pick<Product, "usPrice" | "euPrice" | "gbPrice" | "prices"> | undefined;
  countryCode?: string | null;
  /**
   * 호출자가 **요청한 통화**. EU/GB 티어는 이 값이 EUR/GBP 일 때만 선택한다.
   *
   * ★국가만 보고 고르면 안 되는 이유: 통화 선택(`convertCountryCodeToCurrency`)은
   * `globalEEnabled` flag 로 게이팅되는데(EUR/GBP = GE 전용 통화), 가격 선택이 국가만 보면
   * **flag OFF 인 유로존 사용자에게 통화=USD / 가격=EUR 조합**이 만들어진다 → 정확 매칭 실패로
   * 가격이 통째로 사라진다. 요청 통화를 조건에 넣어 두 결정이 **구조적으로 같이 움직이게** 한다.
   */
  currency?: CurrencyIsoCode;
}): ProductPrice[] => {
  if (!product) return [];
  const cc = (countryCode ?? "").trim().toUpperCase();

  // US 는 **고정가(`usPrice`)만** 쓴다 — 0·미등록이면 기준가로 넘기지 않고 **가격 없음(`[]`)**.
  //
  // EU/GB 와 달리 기준가로 흘려보내면 안 된다: US 의 요청 통화도 USD 라 기준가 `prices[]` 의 USD 가
  // **매칭돼 버린다** → 관세가 빠진 비-US 가격이 US 고정가 자리에 나가고, GE 는 Fixed price 로 받아
  // 관세를 더 얹지 않는다(조용한 언더차지). 종전엔 `usPrice` 객체 존재만 봐서 mgmt 가 미등록 제품에
  // 채워 보내는 `originalAmount: 0` 이 **$0 으로 노출·결제**됐다(prod 2026-10-01: tactglove-dk2-bundle).
  // 가격이 없으면 장바구니는 이용 불가 항목으로 결제를 막고(`CartService.getUnresolvedItems`),
  // 주문 생성은 멈추고(`OrderService.generateOrderItem` 이 null → 자체 결제 draft 가 중단), GE cart-info 는 그 줄을 드롭한다.
  if (cc === "US") {
    return (product.usPrice?.originalAmount ?? 0) > 0
      ? [product.usPrice as ProductPrice]
      : [];
  }

  // EU/GB 는 **값이 실제로 있을 때만** 사용한다. mgmt 가 미등록 제품에 `originalAmount: 0` 을
  // 채워 보내므로(undefined 아님) `> 0` 가드가 없으면 €0/£0 이 그대로 노출된다.
  // 가드에 걸리면 아래 `prices`(USD/JPY/KRW)로 흐르는데, 통화가 EUR/GBP 라 `getPrice` 가 매칭에
  // 실패해 `null` 이 된다 → **가격 미표시**. USD 숫자가 EUR 자리로 새는 일은 없다(#4975 방지).
  if (
    currency === CurrencyIsoCode.GBP &&
    cc === "GB" &&
    (product.gbPrice?.originalAmount ?? 0) > 0
  ) {
    return [product.gbPrice as ProductPrice];
  }
  if (
    currency === CurrencyIsoCode.EUR &&
    EUROZONE_COUNTRY_CODES.includes(cc) &&
    (product.euPrice?.originalAmount ?? 0) > 0
  ) {
    return [product.euPrice as ProductPrice];
  }

  return product.prices ?? [];
};

const getProductPrice = ({
  currency,
  countryCode,
  product,
  furtherDiscountPercentile = 0,
}: {
  currency: CurrencyIsoCode;
  countryCode?: string | null;
  product: Product;
  furtherDiscountPercentile?: number;
}): ProductPrice | null => {
  return getPrice({
    currency,
    prices: getCountryAwarePrices({ product, countryCode, currency }),
    furtherDiscountPercentile,
  });
};

// 목록·업그레이드·번들 머저에서 빼는 번들 — 단품을 고르는 자리에서만 전환되는 세트들.
//   글러브 사이즈 세트: 사이즈 옵션 `all-size` 로 고른다
//   수량 세트(MOLLE Pouch ×2): 단품 수량으로 고른다 — `libs/product/set-variant.ts`
//     ⚠️ 이건 숨김만이 아니라 **머저 차단**이 본론이다. 구성품이 같은 SKU ×2 라
//     머저가 단품 1개로도 세트를 만든다(그 파일 docblock).
const isUnlistedBundle = (slug: string) =>
  slug === ProductSlug.TACTGLOVE_DK2_BUNDLE ||
  slug === ProductSlug.TACTGLOVE_DK3_BUNDLE ||
  isSetVariantSlug(slug);

// 번들 업그레이드 예외 규칙: 특정 제품에 대해 추가로 포함해야 할 번들들
//
// ⚠️ 예외는 "그 제품을 포함하는 상위 번들"에만 쓸 것. 제품이 빠진 번들을 넣으면
// 업그레이드가 아니라 **다른 제품으로 교체**가 된다.
// 예: TactSleeve 에 metro/arizona(슬리브 미포함)를 넣어두면 슬리브를 사러 온
// 사용자가 슬리브 없는 상품을 결제한다. #5068 에서 인라인 노출로 바뀌며 드러나 제거.
// 크로스셀이 필요하면 업그레이드 목록이 아니라 별도 섹션으로 분리할 것.
const BUNDLE_UPGRADE_EXCEPTIONS: Record<string, string[]> = {
  [ProductSlug.FPS_BUNDLE]: [
    ProductSlug.FPS_BUNDLE_TACTVISOR,
    "metro-awakening-bundle",
  ],
  [ProductSlug.STARTER_BUNDLE]: ["arizona-bundle"],
};

const getBundles = (products: Product[], product: Product) => {
  const currentProductSkus = new Set(
    product.skuOptions.map((option) => option.sku),
  );
  const exceptionBundleSlugs = BUNDLE_UPGRADE_EXCEPTIONS[product.slug] || [];

  return products.filter((p) => {
    if (p.productType !== ProductType.BUNDLE) return false;
    if (p.id === product.id) return false;
    if (isUnlistedBundle(p.slug)) return false;

    const containsCurrentProduct = p.skuOptions.some((option) =>
      option.components.some((sku) => currentProductSkus.has(sku)),
    );

    return containsCurrentProduct || exceptionBundleSlugs.includes(p.slug);
  });
};

const getDeviceBundles = (products: Product[], product: Product) => {
  const bundles = getBundles(products, product);
  return bundles.filter((b) =>
    b.skuOptions.every((o) => o.gameResells.length === 0),
  );
};

const getAllBundles = (products: Product[]) => {
  return products.filter(
    (p) => p.productType === ProductType.BUNDLE && !isUnlistedBundle(p.slug),
  );
};

// 게임 번들 중에서 현재 선택된 게임 리셀 옵션과 통화에 맞는 유효한 번들만 필터링하여 반환합니다
// 해당 플랫폼의 통화정보가 없거나 원화인경우 번들에서 제외되게 했습니다.
//
// 번들 전용 게임 키(`isBundleOnlyGameResell`, #6053)는 예외다 — 단독으로 팔지 않으니 가격 행이 없고(`GameUtil.isPriced`), 그래서 **플랫폼만 있으면** 통과시키고 KRW 로는 막지 않는다 — 여기서 보는 것은 게임 키의 판매 제외 국가(`disableCountryList`)뿐이고, 한국 노출은 그 뒤 목록 필터(`isPricedForListing`)가 번들 KRW 가격으로 한 번 더 정한다(EXOSHOCK 은 한국에서 팔지 않는다 — 번들 KRW 0, 2026-11-03 · `docs/bundle-region-policy.md`).
// 키 재고는 호출부가 넘기는 목록(`useSellableGames().bundleGameResells`)이 이미 걸렀다. 국가 판매 제외
// (`disableCountryList`)도 그 목록이 거른다(`filterSellableGames`).
// 일반 게임 키는 종전 규칙 그대로다 — 한국 미판매 안전망(`currency !== "KRW"`)을 그 번들들에는 남긴다.
const getValidBundles = ({
  bundles,
  gameResells,
  currency,
}: {
  bundles: Product[];
  gameResells: GameResell[];
  currency: CurrencyIsoCode;
}) => {
  const validBundles = bundles.filter((bundle) => {
    const bundleGames = bundle.skuOptions.flatMap(
      (option) => option.gameResells ?? [],
    );
    if (bundleGames.length === 0) {
      return true;
    }
    return bundleGames.every((bg) => {
      const gameResell = gameResells.find((gr) => gr.id === bg.id);
      if (!gameResell) return false;
      if (isBundleOnlyGameResell(gameResell)) {
        return gameResell.platformList.some(
          (pl) => pl.platform === bg.platform,
        );
      }
      return gameResell.platformList.some(
        (pl) =>
          pl.platform === bg.platform &&
          currency !== "KRW" &&
          !!GameUtil.findPrice(pl.priceList, currency),
      );
    });
  });

  return validBundles;
};

const filterProductsBySlugs = (products: Product[], slugs: string[]) => {
  if (!slugs.length) return [];
  return products.filter((p) => slugs.includes(p.slug));
};

const getPackageDealProducts = (products: Product[], product?: Product) => {
  if (!product) return [];
  return filterProductsBySlugs(products, product.packageDeals ?? []);
};

const getRecommendedProducts = (products: Product[], product?: Product) => {
  if (!product) return [];
  return filterProductsBySlugs(products, product.recommendedProducts ?? []);
};

// 예외처리가 있습니다.
// 택글러브 DK2 의 경우 세트가 있기 때문에
// 옵션 선택과 상관없이 항상 택글러브 DK2 번들을 포함하도록 합니다.
// 해당 번들 제품을 구성하는 개별 제품들을 반환합니다.
// 예) FPS 번들 -> 택슈트프로 + 택슬리브
const getBundleComponents = (products: Product[], product: Product) => {
  const components = new Set<Product>();
  if (!product) return [];

  product.skuOptions.forEach((option) => {
    option.components.forEach((sku) => {
      const component = products.find(
        (p) =>
          p.skuOptions.some((opt) => opt.sku === sku) &&
          p.slug !== product.slug,
      );
      if (component) {
        components.add(component);
      }
    });
  });

  // 글러브(DK2/DK3): 자기 사이즈 세트(unlisted bundle)를 컴포넌트로 포함.
  // 각 제품은 자기 번들만 (DK2→dk2-bundle, DK3→dk3-bundle) — 교차 오염 방지.
  const ownBundleSlug = CartUtil.getGloveBundleSlug(product.slug);
  if (ownBundleSlug) {
    const ownBundle = products.find((p) => p.slug === ownBundleSlug);
    if (ownBundle) {
      components.add(ownBundle);
    }
  }

  return Array.from(components);
};

/**
 * 번들 카드의 구성품 타일에 쓸 썸네일 — **번들의 모든 SKU 가 이 구성품의 같은 SKU 하나**를 쓸 때만 그 SKU 의 썸네일.
 * 그 밖(색상이 갈리는 번들 · SKU 썸네일 없음)은 `undefined` → 제품 썸네일 그대로.
 *
 * 구성품은 제품 단위로 찾으므로(`getBundleComponents`) 타일이 제품 썸네일을 쓰는데, TactSuit Air 제품 썸네일은
 * 한 색(`air-white-red`)이라 Onyx 만 든 EXOSHOCK 번들에서 다른 색이 보였다(2026-10-28).
 * Onyx · Ash 가 다 있는 번들(Starter · Arizona)은 어느 색을 보일지 정할 수 없어 종전대로 둔다 —
 * 집계(2026-10-28): dev 에선 EXOSHOCK 만 해당, prod 는 해당 0.
 */
const getBundleComponentTileThumbnail = (
  bundle: Product,
  component: Product,
): string | undefined => {
  const componentSkus = new Set(component.skuOptions.map((o) => o.sku));
  const used = bundle.skuOptions.map((o) =>
    o.components.filter((sku) => componentSkus.has(sku)),
  );
  const distinct = new Set(used.flat());
  if (distinct.size !== 1 || used.some((u) => u.length !== 1)) return undefined;
  const sku = [...distinct][0];
  return (
    component.skuOptions.find((o) => o.sku === sku)?.thumbnail || undefined
  );
};

const getBadgeProducts = (products: Product[]) => {
  return products.filter(
    (p) => p.productType === ProductType.ACCESSORY && p.slug.includes("badge"),
  );
};

const getAccessoriesForProduct = (products: Product[], slug: string) => {
  const accessories = products.filter(
    (p) =>
      p.productType === ProductType.ACCESSORY && p.accessoryFor.includes(slug),
  );
  const badgeAccessories = accessories.filter((p) => p.slug.includes("badge"));
  const nonBadgeAccessories = accessories.filter(
    (p) => !p.slug.includes("badge"),
  );
  return { badgeAccessories, nonBadgeAccessories };
};

/**
 * **지금 이 제품 + 이 옵션에서 실제로 제공되는 액세서리.**
 *
 * "화면에 보이는 것"과 "결제되는 것"이 갈리지 않게 하는 단일 출처다.
 * 이 조합(`getAccessoriesForProduct` + 글러브면 `getTactGloveAccessaries`)이
 * 세 군데에 각각 복사돼 있었고, 그중 결제 경로에는 아예 없어서 **화면에서 사라진
 * 액세서리가 그대로 청구**됐다. 실측 사례 두 가지:
 *   - 글러브 사이즈 S→L: 이너글러브(S)가 목록에서 빠지는데 선택은 남음
 *   - TactSuit Air → Camo: camo 는 별도 제품이라 액세서리 섹션이 통째로 사라지는데
 *     "Mesh Lining for TactSuit Air"($20)가 장바구니에 담김
 *
 * ⚠️ `slug` 는 **실효 제품**(camo 면 camo 제품)이어야 한다. 베이스 제품 slug 를 넘기면
 * camo 전환을 못 잡는다 — `accessoryFor` 가 제품 slug 단위라 목록이 통째로 달라진다.
 */
const getOfferedAccessories = (
  products: Product[],
  slug: string,
  options: { type: string; value: string }[],
) => {
  const { badgeAccessories, nonBadgeAccessories } = getAccessoriesForProduct(
    products,
    slug,
  );
  return {
    badgeAccessories,
    // 이너글러브는 사이즈별 **별도 제품**이라 선택 사이즈 것만 남긴다.
    nonBadgeAccessories: CartUtil.isGloveDevice(slug as ProductSlug)
      ? getTactGloveAccessaries(nonBadgeAccessories, options)
      : nonBadgeAccessories,
  };
};

/** 위 목록에 실제로 담긴 SKU 집합 — 선택/청구 검증용. */
const getOfferedAccessorySkus = (
  products: Product[],
  slug: string,
  options: { type: string; value: string }[],
): Set<string> => {
  const { badgeAccessories, nonBadgeAccessories } = getOfferedAccessories(
    products,
    slug,
    options,
  );
  return new Set(
    [...badgeAccessories, ...nonBadgeAccessories].flatMap((p) =>
      p.skuOptions.map((o) => o.sku),
    ),
  );
};

/**
 * **결제에 실을 액세서리인가** — 세 결제 경로가 공유하는 단일 술어.
 * (`addAccessoriesToCart` · `useBuyTactSuitCheckout` · `buildAccessoryNaverPayItems`)
 *
 * 예전엔 셋이 조건이 갈렸다 — 앞 둘은 `selected` 만, 네이버페이만 `quantity > 0` 까지.
 * 오늘은 `Accessories.tsx` 가 수량 0 이면 `selected: false` 로 붙여둬서 잠복 상태지만,
 * `selected: true, quantity: 0` 인 엔트리가 생기면 두 경로는 수량 0 주문 항목을 만들고
 * 한 경로는 버린다. 술어를 여기 하나로 둔다.
 */
const isChargeableAccessory = (
  accessory: { selected: boolean; quantity: number; sku: string },
  offeredSkus: Set<string>,
) =>
  accessory.selected &&
  accessory.quantity > 0 &&
  offeredSkus.has(accessory.sku);

const isMadeForMeta = (slug: ProductSlug) => {
  return MADE_FOR_META_DEVICES.some((p) => p === slug);
};

// ⚠️ 판정 축이 `productType` 이 아니라 `accessoryFor` 다 — 그래서 **번들도 걸린다.**
// MOLLE Pouch (Set)(`BUNDLE`)이 `accessoryFor: [starter-bundle-camo, tactsuit-air-camo]` 를 갖고
// 있어 `/tactsuit`·`/tactsuit/accessories` 그리드에 따로 떴다. 숨긴 번들(`isUnlistedBundle`)은 뺀다.
// 실측(2026-10-01 · admin-apis shop-products): `accessoryFor` 가 있는 BUNDLE 은 dev 1건(그 세트) ·
// prod 0건 — 이 조건으로 빠지는 다른 제품은 없다.
//
// ★**사이즈 라인은 대표 1장으로 접는다** (#5149 · `libs/product/size-line.ts`). Inner Gloves for
// TactGlove DK3 가 S/M/L/XL **별도 제품 4개**라 그리드에 카드가 4장 떴다. 나머지 사이즈는 대표
// PDP 의 사이즈 선택지로 고른다. 카드 이름·썸네일은 사이즈 무관 라인 값으로 바꾼다 — 대표가 (S) 라도
// 카드가 「(S)」를 말하거나 그림에 「S」가 박혀 있으면 S 만 파는 것으로 읽힌다.
// ⚠️ 이 접기는 **목록 전용**이다. DK3 PDP·카트시트·카트 추천은 `getAccessoriesForProduct` /
// `getTactGloveAccessaries` 로 「고른 글러브 사이즈의 이너글러브 1개」를 따로 고르므로 여기를 안 탄다.
/**
 * 액세서리 목록 **맨 앞에 고정**할 제품 — 배열 순서 = 노출 순서. 나머지는 API 순서 그대로 뒤에 온다.
 * 목록에 없는 제품(미등록·판매중지·가격 없음으로 걸러진 것)은 그냥 건너뛴다 — 자리를 비우지 않는다.
 *
 * 소비처는 `getAccessories` 를 거치는 두 목록뿐이다(`/tactsuit/accessories` · `/tactsuit` 의 액세서리 탭).
 * 탭 필터(For Pro / For Air)는 `filter` 라 상대 순서가 유지돼 각 탭에서도 맨 앞이다.
 * MOLLE Pouch Camo 는 단품·세트 중 **목록에 나가는 쪽**이 이 자리를 갖는다(세트는 단품의 순위를 물려받는다 —
 * `pinnedRank`). 어느 쪽이 나가는지는 `getAccessories` 의 `setFirstBases`.
 */
const PINNED_ACCESSORY_SLUGS: readonly string[] = [
  ProductSlug.COOLING_FAN,
  ProductSlug.MOLLE_POUCH_FOR_TACTSUIT_AIR_CAMO,
];

const pinnedRank = (slug: string) => {
  const i = PINNED_ACCESSORY_SLUGS.indexOf(setBaseOf(slug) ?? slug);
  return i === -1 ? PINNED_ACCESSORY_SLUGS.length : i;
};

export type AccessoryListingOptions = {
  /**
   * **세트를 대표 카드로 내보낼 단품 슬러그**(#6008). 여기 든 단품은 목록에서 빠지고 그 수량 세트
   * (`SET_VARIANT_BY_SLUG`)가 그 자리에 선다 — 카드의 이름·사진·가격·「담기」가 전부 세트 기준이 된다.
   *
   * 판정(세트 재고 · 가격 · 세트가 더 쌈)은 통화·국가가 필요해 이 순수 함수 밖에서 한다 —
   * 화면은 `useSetFirstBases`(`canPackSet` · 카트 묶기와 같은 술어). 비우면 종전대로 단품 카드다.
   * 거래가 성립하지 않으면 세트를 앞세우지 않는다 — 품절·가격 없음 세트를 대표로 세우면 목록에서
   * 그 제품을 살 길이 사라진다.
   */
  setFirstBases?: ReadonlySet<string>;
};

const getAccessories = (
  products: Product[],
  { setFirstBases }: AccessoryListingOptions = {},
) => {
  const listed = (slug: string) => {
    if (setFirstBases?.has(slug)) return false;
    const base = setBaseOf(slug);
    if (base) return !!setFirstBases?.has(base);
    return !isUnlistedBundle(slug);
  };
  return (
    products
      .filter(
        (p) => p.accessoryFor && p.accessoryFor.length > 0 && listed(p.slug),
      )
      .flatMap((p) => {
        const line = sizeLineOf(p.slug);
        if (!line) return [p];
        const representative = sizeLineRepresentative(products, line);
        return representative?.slug === p.slug
          ? [{ ...p, name: line.name, thumbnail: line.thumbnail }]
          : [];
      })
      // 안정 정렬 — 고정 대상만 앞으로, 나머지는 API 순서 유지(ES2019+ `sort` 는 안정).
      .sort((a, b) => pinnedRank(a.slug) - pinnedRank(b.slug))
  );
};

const getSkuByOptions = ({
  product,
  options,
  gameResells = [],
}: {
  product: Product;
  options: { type: string; value: string }[];
  gameResells: { id: string; platform: string }[];
}): string | null => {
  const skus = getProductSkus({ products: [product], options, gameResells });
  if (skus.length === 1) return skus[0];

  return getProductSkuBySkuOptions({
    skuOptions: product.skuOptions,
    skus,
    gameResellOptions: gameResells,
  });
};

// skuOption 안에 있는 skus 배열과 전달된 skus 배열을 비교하여
// 일치할때 skuOption의 sku를 반환하는 유틸 함수
const getProductSkuBySkuOptions = ({
  skuOptions,
  skus,
  gameResellOptions = [],
}: {
  skuOptions: SkuOption[];
  skus: string[];
  gameResellOptions?: { id: string; platform: string }[];
}): string | null => {
  const skusSet = new Set(skus);

  const matchedOption = skuOptions.find((option) => {
    if (option.components.length !== skus.length) return false;
    if (option.gameResells.length > 0) {
      return (
        option.components.every((sku) => skusSet.has(sku)) &&
        option.gameResells.length === gameResellOptions.length &&
        gameResellOptions.every((gr) =>
          option.gameResells.some(
            (gro) => gr.id === gro.id && gr.platform === gro.platform,
          ),
        )
      );
    } else {
      return option.components.every((sku) => skusSet.has(sku));
    }
  });

  return matchedOption?.sku ?? null;
};

// skuOptions 안에 있는 각각의 skus 배열과 전달된 options, gameResells 배열을 비교하여
// 일치하는 skus들을 모두 수집하여 반환하는 유틸 함수
const getProductSkus = ({
  products,
  options,
  gameResells = [],
}: {
  products: Product[];
  options: { type: string; value: string }[];
  gameResells: { id: string; platform: string }[];
}) => {
  if (!products.length) return [];
  return products.reduce<string[]>((acc, product) => {
    product.skuOptions.forEach((skuOption) => {
      // SkuOption 의 옵션들이 모두 전달된 옵션들에 포함되는지 확인
      const optionsMatches = skuOption.options.every((option) => {
        return options.some(
          (opt) => opt.value.toLowerCase() === option.toLowerCase(),
        );
      });
      let gameResellMatches = true;
      if (skuOption.gameResells.length > 0) {
        gameResellMatches =
          skuOption.gameResells.length === gameResells.length &&
          gameResells.every((gr) =>
            skuOption.gameResells.some(
              (gro) => gr.id === gro.id && gr.platform === gro.platform,
            ),
          );
      }
      if (optionsMatches && gameResellMatches) {
        acc.push(skuOption.sku);
      }
    });

    return acc;
  }, []);
};

// skuOption 의 options 배열을 { type, value } 형식의 배열로 변환합니다
// 첫 번째 옵션은 "color", 두 번째 옵션은 "size"로 간주합니다
// 제품 인터페이스의 skuOption.options 가 명시적으로 type, value 의 쌍으로 변경전까지 사용합니다.
const getOptionsFromSkuOption = (
  skuOption: SkuOption,
): { type: string; value: string }[] => {
  return skuOption.options.map((opt, idx) => ({
    type: idx === 0 ? "color" : "size",
    value: opt,
  }));
};

const findProductSku = ({
  candidateSkus,
  product,
}: {
  product: Product;
  candidateSkus: string[];
}) => {
  const { skuOptions } = product;
  if (skuOptions.length === 1) {
    return skuOptions[0].sku;
  } else {
    if (candidateSkus.length === 1) {
      return candidateSkus[0];
    }
    const productSkus = product.skuOptions.flatMap((o) => o.sku);
    return candidateSkus.find((sku) => productSkus.includes(sku)) || null;
  }
};

// products 를 순환하면서 해당 제품의 skuOptions 에서 sku 가 일치하는 제품을 찾아 반환합니다
const getProductBySku = (products: Product[], sku: string): Product | null => {
  for (const product of products) {
    const foundOption = product.skuOptions.find((option) => option.sku === sku);
    if (foundOption) {
      return product;
    }
  }
  return null;
};

// 이너글러브 slug 의 사이즈 접미사. 제품 세대(dk2/dk3/…) 무관하게 매칭.
// slug 예: inner-gloves-for-tactglove-dk3-s
const GLOVE_INNER_SIZE_SUFFIX: Record<string, string> = {
  small: "-s",
  medium: "-m",
  large: "-l",
  "x-large": "-xl",
};

// slug 이 해당 사이즈의 이너글러브인지. enum 상수 대신 접미사 매칭이라
// 신규 글러브 세대(dk4 등) 추가 시 코드 변경 0 (제품 slug 만 규칙 따르면 됨).
const isInnerGloveSlugForSize = (slug: string, size: string): boolean => {
  const suffix = GLOVE_INNER_SIZE_SUFFIX[size];
  return (
    !!suffix &&
    slug.includes("inner-gloves-for-tactglove") &&
    slug.endsWith(suffix)
  );
};

const getTactGloveAccessaries = (
  products: Product[],
  options: { type: string; value: string }[],
) => {
  const sizeOption = options
    .filter((opt) => !!opt.value)
    .find((opt) => opt.type === "size");
  if (!sizeOption) return [];
  const accessory = products.find((p) =>
    isInnerGloveSlugForSize(p.slug, sizeOption.value),
  );
  if (accessory) return [accessory];
  return products;
};

const getSkuName = (product: Product, sku: string): string => {
  const skuOption = product.skuOptions.find((option) => option.sku === sku);

  return skuOption ? skuOption.name : "";
};

const isAudioAccessoryIncluded = (product: Product): boolean => {
  const components = product.skuOptions.flatMap((option) => option.components);
  return components.some(
    (sku) =>
      sku === "tactsuit-pro" ||
      sku.startsWith("starter-bundle") ||
      sku.startsWith("tactsuit-air") ||
      sku === "fps-bundle",
  );
};

const getSkuThumbnail = (products: Product[], sku: string): string | null => {
  const allSkuOptions = products.flatMap((p) => p.skuOptions);
  const skuOption = allSkuOptions.find((option) => option.sku === sku);
  if (!skuOption) return null;
  return skuOption.thumbnail;
};

const getThumbnail = ({
  products,
  sku,
  slug,
}: {
  products: Product[];
  sku: string;
  slug: string;
}): string | null => {
  if (sku) {
    const thumbnail = ProductUtil.getSkuThumbnail(products, sku);
    if (thumbnail) return thumbnail;
  }
  const product = products.find((p) => p.slug === slug);
  if (!product) return null;
  return product.thumbnail;
};

const getOptions = (skuOptions: SkuOption[]) => {
  return Array.from(new Set(skuOptions.flatMap((option) => option.options)));
};

const getGameBundles = (products: Product[]) => {
  return products.filter((product) =>
    product.skuOptions.every((o) => o.gameResells.length > 0),
  );
};

const createProductUtil = () => {
  return {
    // SKU 기준 핵심 함수
    getSkuOption,
    getStockBySku,
    getShippingInfoBySku,
    isProductSoldOut,

    // 기타 유틸
    getBundles,
    // 카트 자동 번들 병합(BundleMerger)도 같은 기준을 써야 한다. 노출 목록에서
    // 뺀 번들이 병합 후보로는 살아 있으면 "숨긴 제품이 카트에 나타난다".
    isUnlistedBundle,
    isMadeForMeta,
    getBundleComponents,
    getPackageDealProducts,
    filterProductsBySlugs,
    getRecommendedProducts,
    getBadgeProducts,
    getAccessoriesForProduct,
    getOfferedAccessories,
    getOfferedAccessorySkus,
    isChargeableAccessory,
    getAccessories,
    // ⚠️ `getPrice` 는 **의도적으로 export 하지 않는다**(#5179 리뷰 §3). 기준가 배열을 직접 받는
    // 시그니처라 국가별 티어(`getCountryAwarePrices`)를 건너뛰기 쉬운데, 그러면 EU/GB 에서
    // 정확 매칭 실패로 가격이 통째로 사라진다(카트시트 throw / 배지 소멸 사례 4건).
    // 가격이 필요하면 **항상 `getProductPrice`** 를 쓸 것 — countryCode 를 강제로 받는다.
    getProductImages,
    getEstimatedShippingTime,
    getSkuShippingDate,
    categorizeAccessories,
    categorizeDevices,
    categorizeTactSuitAccessories,
    getProductSkuBySkuOptions,
    getProductSkus,
    getOptionsFromSkuOption,
    getAllBundles,
    findProductSku,
    getProductGameResellIds,
    getBundleComponentTileThumbnail,
    categorizeBundles,
    getProductPrice,
    getCountryAwarePrices,
    getProductBySku,
    getTactGloveAccessaries,
    isInnerGloveSlugForSize,
    getSkuName,
    isAudioAccessoryIncluded,
    getValidBundles,
    getOptions,
    getSkuThumbnail,
    getThumbnail,
    getGameBundles,
    getDeviceBundles,
    getSkuByOptions,
  };
};

export const ProductUtil = createProductUtil();
